
import React, { useState, useEffect, useRef } from 'react';
import { findEvidence } from '../services/geminiService';
import type { View, EvidenceSearchResult, ProfessionalFocus, EvidenceArticle } from '../types';
import AiDisclaimer from './AiDisclaimer';
import { useAuth } from '../contexts/AuthContext';

const SmallLogoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);

const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
const ExternalLinkIcon: React.FC = () => (<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>);
const BrainIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.871 14.129c-1.994-1.994-1.994-5.229 0-7.223s5.229-1.994 7.223 0c1.994 1.994 1.994 5.229 0 7.223-1.994 1.994-5.229 1.994-7.223 0zM12 12v.01M12 4.5V2m0 20v-2.5m-7-13l-1.5-1.5m1.5 1.5l-1.5 1.5m14.5-1.5l1.5-1.5m-1.5 1.5l1.5 1.5M4.5 12H2m20 0h-2.5" /></svg>;
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;

const PROFESSIONAL_FOCUSES: ProfessionalFocus[] = ['Pharmacist', 'Physician', 'Nurse', 'Researcher'];
const SEARCH_SOURCES = ['JAMA', 'PubMed', 'NEJM'];
const LOADING_MESSAGES = ['Analyzing clinical query...', 'Searching PubMed for trials...', 'Cross-referencing with JAMA...', 'Synthesizing key findings...', 'Finalizing evidence summary...'];

interface EvidenceSearchProps {
    initialQuery: string | null;
    onNavigate: (view: View) => void;
}

const AgentLoader: React.FC<{ message: string }> = ({ message }) => (
    <div className="text-center py-20 flex flex-col items-center justify-center">
        <BrainIcon />
        <p className="text-lg font-semibold text-gray-300 mt-4">{message}</p>
        <div className="w-1/3 h-1 bg-blue-900/50 rounded-full mt-4 overflow-hidden">
            <div className="h-1 bg-yellow-400" style={{ width: '100%', animation: 'indeterminate 2s infinite linear' }}></div>
        </div>
    </div>
);

const ArticleCard: React.FC<{ article: EvidenceArticle }> = ({ article }) => {
    const sourceColors: Record<string, string> = {
        JAMA: 'border-red-500/50 bg-red-500/10 text-red-300',
        PubMed: 'border-blue-500/50 bg-blue-500/10 text-blue-300',
        NEJM: 'border-green-500/50 bg-green-500/10 text-green-300',
    };
    return (
        <div className="bg-[#15243E] p-6 rounded-xl border border-blue-900/50 hover:border-yellow-400/50 transition-colors">
            <div className="flex justify-between items-start gap-4">
                <h3 className="text-lg font-bold text-white flex-grow">{article.title}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-md border ${sourceColors[article.source]}`}>{article.source}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2 font-mono">
                <span>{article.authors}</span> &bull; <span>{new Date(article.publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</span>
            </div>
            <p className="text-sm text-gray-300 mt-4">{article.summary}</p>
            <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-sm text-yellow-400 font-semibold mt-4 inline-flex items-center gap-2 group fancy-underline">
                Read Full Article <ExternalLinkIcon />
            </a>
        </div>
    );
};


const UpgradeNotice: React.FC = () => {
    const { setIsUpgradeModalOpen } = useAuth();
    return (
        <div className="text-center py-20 flex flex-col items-center justify-center bg-[#15243E] rounded-xl border border-blue-900/50">
            <LockIcon />
            <h2 className="text-2xl font-bold text-white mt-4">This is a Pro Feature</h2>
            <p className="text-gray-400 mt-2 max-w-md">The Evidence Search Agent is available exclusively for Pro members. Upgrade your plan to access our most powerful AI research assistant.</p>
            <button onClick={() => setIsUpgradeModalOpen(true)} className="mt-6 bg-yellow-400 text-gray-900 font-bold py-2.5 px-8 rounded-lg transition-transform transform hover:scale-105 hover:shadow-lg hover:shadow-yellow-500/20">
                Upgrade to Pro
            </button>
        </div>
    );
}

const EvidenceSearch: React.FC<EvidenceSearchProps> = ({ initialQuery, onNavigate }) => {
    const { subscription } = useAuth();
    const [query, setQuery] = useState(initialQuery || '');
    const [professionalFocus, setProfessionalFocus] = useState<ProfessionalFocus>('Pharmacist');
    const [selectedSources, setSelectedSources] = useState<string[]>(SEARCH_SOURCES);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<EvidenceSearchResult | null>(null);
    const hasSearched = useRef(false);
    
    const hasAccess = subscription?.plan === 'Pro' || subscription?.plan === 'Teams';

    useEffect(() => {
        if (initialQuery && !hasSearched.current && hasAccess) {
            hasSearched.current = true;
            handleSearch();
        }
    }, [initialQuery, hasAccess]);

    useEffect(() => {
        let interval: number;
        if (isLoading) {
            let i = 0;
            interval = window.setInterval(() => {
                i = (i + 1) % LOADING_MESSAGES.length;
                setLoadingMessage(LOADING_MESSAGES[i]);
            }, 1800);
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    const handleSearch = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!query.trim()) return;

        setIsLoading(true);
        setError(null);
        setResults(null);

        try {
            const data = await findEvidence(query, selectedSources, professionalFocus);
            setResults(data);
        } catch (err) {
            setError('The AI agent encountered an error. Please try refining your search or try again later.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSource = (source: string) => {
        setSelectedSources(prev =>
            prev.includes(source)
                ? prev.filter(s => s !== source && prev.length > 1)
                : [...prev, source]
        );
    };

    if (!hasAccess) {
        return (
             <div className="py-6">
                <header className="mb-8">
                    <button onClick={() => onNavigate('library')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold mb-4">
                        <BackIcon /> Back to Library
                    </button>
                    <h1 className="text-4xl font-bold">Evidence Search Agent</h1>
                    <p className="text-gray-400 mt-2 max-w-2xl">Your AI agent for finding clinical evidence from top-tier medical journals.</p>
                </header>
                <UpgradeNotice />
            </div>
        );
    }
    
    return (
        <div className="py-6">
            <header className="mb-8">
                 <div className="flex justify-between items-start mb-4">
                    <button onClick={() => onNavigate('library')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold">
                        <BackIcon /> Back to Library
                    </button>
                     <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-2 opacity-50 hover:opacity-100 transition-all cursor-pointer hover:scale-105">
                         <SmallLogoIcon />
                         <span className="text-sm font-bold text-white">Dr.<span className="text-yellow-400">Rx</span><sup className="text-xs -top-2">TM</sup></span>
                    </button>
                </div>
                <h1 className="text-4xl font-bold">Evidence Search Agent</h1>
                <p className="text-gray-400 mt-2 max-w-2xl">Your AI agent for finding clinical evidence from top-tier medical journals.</p>
            </header>

            <div className="sticky top-0 bg-[#101D35]/90 backdrop-blur-lg z-10 py-4 mb-8 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-y border-blue-900/50">
                <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Ask a clinical question..."
                            className="w-full bg-[#1E2E4A] border border-[#3A4A67] rounded-full p-3 pl-12 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"><SearchIcon /></div>
                    </div>
                    <button type="submit" className="bg-yellow-400 text-gray-900 font-bold py-3 px-8 rounded-full hover:bg-yellow-300 transition-transform transform hover:scale-105" disabled={isLoading}>
                        {isLoading ? 'Searching...' : 'Search Evidence'}
                    </button>
                </form>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-4">
                     <div>
                        <span className="text-sm font-semibold text-gray-400 mr-3">Sources:</span>
                        {SEARCH_SOURCES.map(source => (
                             <label key={source} className="inline-flex items-center gap-2 mr-4 cursor-pointer">
                                <input type="checkbox" checked={selectedSources.includes(source)} onChange={() => toggleSource(source)} className="sr-only peer" />
                                <div className="w-5 h-5 bg-[#2c3a53] rounded-md border border-blue-800/50 peer-checked:bg-blue-600 peer-checked:border-blue-500 transition-all flex items-center justify-center"><CheckIcon /></div>
                                <span className="text-sm font-medium">{source}</span>
                            </label>
                        ))}
                    </div>
                     <div>
                        <label className="text-sm font-semibold text-gray-400 mr-2">Focus:</label>
                        <select value={professionalFocus} onChange={e => setProfessionalFocus(e.target.value as ProfessionalFocus)} className="bg-[#1E2E4A] border border-[#3A4A67] rounded-md p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-yellow-400">
                            {PROFESSIONAL_FOCUSES.map(focus => <option key={focus} value={focus}>{focus}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {isLoading && <AgentLoader message={loadingMessage} />}
            {error && <div className="text-center py-20 text-red-400 bg-[#15243E] rounded-xl border border-red-900/50">{error}</div>}
            
            {results && (
                <div className="space-y-8">
                    <section>
                        <h2 className="text-2xl font-bold mb-4">AI Synthesis</h2>
                        <div className="bg-[#15243E] p-6 rounded-xl border border-blue-900/50">
                            <ul className="list-disc list-inside space-y-2 text-gray-300"
                                dangerouslySetInnerHTML={{ __html: results.synthesis.replace(/•/g, '<li>').replace(/<\/li> /g, '</li>') }}
                            />
                        </div>
                    </section>
                    <section>
                        <h2 className="text-2xl font-bold mb-4">Relevant Articles</h2>
                        <div className="space-y-6">
                            {results.articles.map(article => <ArticleCard key={article.id} article={article} />)}
                        </div>
                    </section>
                </div>
            )}
            
            <div className="mt-8">
                <AiDisclaimer />
            </div>

        </div>
    );
};

export default EvidenceSearch;
